import React from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Globe, Check, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Language } from '@/services/languageMapping';

interface ChatHeaderProps {
  selectedLanguage: Language;
  setSelectedLanguage: (language: Language) => void;
  availableLanguages: Language[];
  autoTranslate: boolean;
  onToggleAutoTranslate: () => void;
  onClose: () => void;
}

/**
 * Chat panel header with language selector and auto-translate toggle
 */
export const ChatHeader: React.FC<ChatHeaderProps> = ({
  selectedLanguage,
  setSelectedLanguage,
  availableLanguages,
  autoTranslate,
  onToggleAutoTranslate,
  onClose
}) => {
  return (
    <>
      <div className="p-3 border-b border-gray-700 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <h2 className="font-medium">Chat</h2>

          {/* Language selection dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 gap-1 text-xs"
              >
                <span className="mr-1">{selectedLanguage.flag}</span>
                <span>{selectedLanguage.nativeAbbr || selectedLanguage.code}</span>
                <ChevronDown className="h-4 w-4 opacity-50"/>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-slate-800 border-slate-700 max-h-60 overflow-y-auto">
              {availableLanguages.map((language: Language) => (
                <DropdownMenuItem
                  key={language.code}
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => setSelectedLanguage(language)}
                >
                  <span className="mr-1">{language.flag}</span>
                  <span>{language.name}</span>
                  {language.code === selectedLanguage.code && (
                    <Check className="h-4 w-4 ml-auto"/>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center space-x-2">
          {/* Auto-translate toggle */}
          <div className="flex items-center mr-2">
            <Switch
              id="auto-translate"
              checked={autoTranslate}
              onCheckedChange={onToggleAutoTranslate}
              className="scale-75"
            />
            <Label htmlFor="auto-translate" className="ml-1 text-xs">
              Auto
            </Label>
          </div>

          {/* Close chat button */}
          <button
            className="text-muted-foreground hover:text-foreground transition-colors"
            onClick={onClose}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>

      {/* Translation info bar */}
      {autoTranslate && (
        <div className="px-3 py-2 bg-slate-800/50 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center text-xs text-slate-300">
            <Globe className="h-3 w-3 mr-1 text-slate-400"/>
            Auto-translating to {selectedLanguage.name}
          </div>
        </div>
      )}
    </>
  );
};
